import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { apiService } from '../services/api';
import { 
  ArrowLeft, 
  ArrowRight, 
  Plus, 
  Minus, 
  RotateCcw, 
  Check, 
  User,
  Users,
  PenTool,
  Gift,
  Zap,
  Shield,
  Star,
  Sparkles,
  BookOpen,
  Calendar,
  Bell
} from 'lucide-react';

interface FormStep {
  id: number;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  completed: boolean;
}

const ProgressiveWaiverForm: React.FC = () => {
  const navigate = useNavigate();
  const signaturePadRef = useRef<SignatureCanvas>(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    emergencyContact: '',
    emergencyPhone: '',
    numKids: 0,
    kidsAges: [] as number[],
    signedFor: 'self' as 'self' | 'family' | 'group',
    marketingConsent: {
      vrPromotions: false,
      stemWorkshops: false,
      specialEvents: false,
      frequency: 'monthly' as 'weekly' | 'monthly' | 'events-only'
    },
    interests: [] as string[],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const steps: FormStep[] = [
    {
      id: 1,
      title: "Personal Info",
      subtitle: "Tell us about yourself",
      icon: <User className="w-6 h-6" />,
      completed: completedSteps.includes(1)
    },
    {
      id: 2,
      title: "Group Details",
      subtitle: "Who's joining the adventure?",
      icon: <Users className="w-6 h-6" />,
      completed: completedSteps.includes(2)
    },
    {
      id: 3,
      title: "Stay Connected",
      subtitle: "Get exclusive offers & updates",
      icon: <Gift className="w-6 h-6" />,
      completed: completedSteps.includes(3)
    },
    {
      id: 4,
      title: "Digital Signature",
      subtitle: "Almost there! Sign to complete",
      icon: <PenTool className="w-6 h-6" />,
      completed: completedSteps.includes(4)
    }
  ];

  const interests = [
    { id: 'gaming', label: 'VR Gaming', icon: '🎮' },
    { id: 'education', label: 'Educational VR', icon: '📚' },
    { id: 'fitness', label: 'VR Fitness', icon: '💪' },
    { id: 'art', label: 'Creative Arts', icon: '🎨' },
    { id: 'science', label: 'Science Experiments', icon: '🔬' },
    { id: 'coding', label: 'Programming', icon: '💻' },
  ];

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.name && formData.email);
      case 2:
        return true; // Optional step
      case 3:
        return true; // Optional step
      case 4:
        return !signaturePadRef.current?.isEmpty();
      default:
        return true;
    }
  };

  const handleStepComplete = (step: number) => {
    if (validateStep(step) && !completedSteps.includes(step)) {
      setCompletedSteps(prev => [...prev, step]);
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      handleStepComplete(currentStep);
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'phone' || name === 'emergencyPhone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      if (name.startsWith('marketingConsent.')) {
        const field = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          marketingConsent: {
            ...prev.marketingConsent,
            [field]: checkbox.checked
          }
        }));
      } else if (name === 'interests') {
        const interest = checkbox.value;
        setFormData(prev => ({
          ...prev,
          interests: checkbox.checked 
            ? [...prev.interests, interest]
            : prev.interests.filter(i => i !== interest)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'numKids' ? parseInt(value) || 0 : value,
      }));
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      if (!validateStep(4)) {
        throw new Error('Please provide your signature');
      }

      const signatureData = signaturePadRef.current?.toDataURL();
      
      if (!signatureData) {
        throw new Error('Failed to capture signature');
      }
      
      const waiverData = {
        ...formData,
        signature: signatureData,
      };

      await apiService.createWaiver(waiverData);
      
      navigate('/confirmation', { 
        state: { 
          waiverData: {
            name: formData.name,
            email: formData.email,
            signedAt: new Date().toISOString(),
            marketingConsent: formData.marketingConsent,
            interests: formData.interests
          }
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit waiver');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Let's Get to Know You!</h3>
              <p className="text-slate-300">We need some basic information to get started</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-sm"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-sm"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-sm"
                  placeholder="(555) 987-6543"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Who's Joining the Adventure?</h3>
              <p className="text-slate-300">Tell us about your group for the best VR experience</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Signing waiver for:
              </label>
              <select
                name="signedFor"
                value={formData.signedFor}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm"
              >
                <option value="self" className="bg-slate-800">Myself only</option>
                <option value="family" className="bg-slate-800">My family</option>
                <option value="group" className="bg-slate-800">My group</option>
              </select>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <label className="block text-lg font-medium text-white mb-4">
                Number of Kids (Ages 5-17)
              </label>
              <div className="flex items-center justify-center space-x-8">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, numKids: Math.max(0, prev.numKids - 1) }))}
                  className="p-4 bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all backdrop-blur-sm text-white hover:scale-110"
                >
                  <Minus className="w-6 h-6" />
                </button>
                <div className="text-center">
                  <div className="text-4xl font-bold text-white bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                    {formData.numKids}
                  </div>
                  <div className="text-slate-300 text-sm">Kids</div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, numKids: prev.numKids + 1 }))}
                  className="p-4 bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all backdrop-blur-sm text-white hover:scale-110"
                >
                  <Plus className="w-6 h-6" />
                </button>
              </div>
            </div>

            {formData.numKids > 0 && (
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <label className="block text-lg font-medium text-white mb-4">
                  Ages of Kids
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Array.from({ length: formData.numKids }, (_, index) => (
                    <div key={index} className="relative">
                      <input
                        type="number"
                        min="5"
                        max="17"
                        value={formData.kidsAges[index] || 8}
                        onChange={(e) => {
                          const newAges = [...formData.kidsAges];
                          newAges[index] = parseInt(e.target.value) || 8;
                          setFormData(prev => ({ ...prev, kidsAges: newAges }));
                        }}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm text-center"
                        placeholder="Age"
                      />
                      <div className="text-xs text-slate-400 text-center mt-1">Kid {index + 1}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Stay Connected & Save!</h3>
              <p className="text-slate-300">Get exclusive offers, early access, and workshop updates</p>
            </div>

            {/* Marketing Consent Options */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-2xl p-6 border border-green-500/20">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="vrPromotions"
                    name="marketingConsent.vrPromotions"
                    checked={formData.marketingConsent.vrPromotions}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 text-green-500 bg-transparent border-2 border-green-500/50 rounded focus:ring-green-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <label htmlFor="vrPromotions" className="text-white font-semibold cursor-pointer flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-yellow-400" />
                      VR Session Discounts & New Experiences
                    </label>
                    <p className="text-slate-300 text-sm mt-1">Get 15% off your next visit + early access to new VR games and experiences</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="stemWorkshops"
                    name="marketingConsent.stemWorkshops"
                    checked={formData.marketingConsent.stemWorkshops}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 text-purple-500 bg-transparent border-2 border-purple-500/50 rounded focus:ring-purple-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <label htmlFor="stemWorkshops" className="text-white font-semibold cursor-pointer flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-blue-400" />
                      STEM Workshops & Educational Programs
                    </label>
                    <p className="text-slate-300 text-sm mt-1">Be first to register for coding camps, robotics workshops, and science experiments</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl p-6 border border-orange-500/20">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="specialEvents"
                    name="marketingConsent.specialEvents"
                    checked={formData.marketingConsent.specialEvents}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 text-orange-500 bg-transparent border-2 border-orange-500/50 rounded focus:ring-orange-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <label htmlFor="specialEvents" className="text-white font-semibold cursor-pointer flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-orange-400" />
                      Special Events & Birthday Parties
                    </label>
                    <p className="text-slate-300 text-sm mt-1">Birthday party packages, holiday events, and group booking specials</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Interests Selection */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-400" />
                What interests you most? (Optional)
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {interests.map((interest) => (
                  <label key={interest.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value={interest.id}
                      checked={formData.interests.includes(interest.id)}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-500 bg-transparent border-2 border-white/30 rounded focus:ring-blue-500"
                    />
                    <span className="text-2xl">{interest.icon}</span>
                    <span className="text-white text-sm">{interest.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Frequency Preference */}
            {(formData.marketingConsent.vrPromotions || formData.marketingConsent.stemWorkshops || formData.marketingConsent.specialEvents) && (
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Bell className="w-5 h-5 mr-2 text-blue-400" />
                  How often would you like to hear from us?
                </h4>
                <div className="space-y-2">
                  {[
                    { value: 'weekly', label: 'Weekly updates (get the latest offers)' },
                    { value: 'monthly', label: 'Monthly newsletter (recommended)' },
                    { value: 'events-only', label: 'Special events only' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="marketingConsent.frequency"
                        value={option.value}
                        checked={formData.marketingConsent.frequency === option.value}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          marketingConsent: { ...prev.marketingConsent, frequency: e.target.value as any }
                        }))}
                        className="w-4 h-4 text-blue-500 bg-transparent border-2 border-white/30"
                      />
                      <span className="text-white text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <PenTool className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Almost There!</h3>
              <p className="text-slate-300">Sign below to complete your waiver and start your VR adventure</p>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="bg-white/10 border-2 border-dashed border-white/20 rounded-xl p-6">
                <p className="text-sm text-slate-300 mb-4 text-center">
                  Please sign in the box below using your mouse, trackpad, or finger (on touch devices)
                </p>
                
                <div className="border border-white/30 rounded-xl overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={signaturePadRef}
                    canvasProps={{
                      className: 'signature-canvas w-full',
                      width: 500,
                      height: 200,
                    }}
                    backgroundColor="white"
                  />
                </div>
                
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={() => signaturePadRef.current?.clear()}
                    className="flex items-center px-6 py-3 text-sm text-slate-300 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all backdrop-blur-sm"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Clear Signature
                  </button>
                </div>
              </div>
            </div>

            {/* Liability Waiver Text */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <h4 className="font-semibold text-white mb-4 text-lg flex items-center">
                <Shield className="w-5 h-5 mr-2 text-green-400" />
                Liability Waiver Agreement
              </h4>
              <div className="text-sm text-slate-300 max-h-32 overflow-y-auto space-y-2 bg-white/5 p-4 rounded-xl">
                <p>
                  I acknowledge that participation in virtual reality activities involves inherent risks, including but not limited to physical injury, motion sickness, and disorientation.
                </p>
                <p>
                  I voluntarily assume all risks and hazards incidental to such participation and hereby waive, release, and discharge Immersa VR Center from any and all liability, claims, demands, actions, or causes of action.
                </p>
                <p>
                  This waiver is valid for one year from the date of signing and covers all visits during this period.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-slate-300 hover:text-white mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                    currentStep === step.id 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 border-purple-400' 
                      : step.completed
                        ? 'bg-green-500 border-green-400'
                        : 'bg-white/10 border-white/20'
                  }`}>
                    {step.completed ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : currentStep === step.id ? (
                      <Sparkles className="w-6 h-6 text-white animate-pulse" />
                    ) : (
                      step.icon
                    )}
                    {currentStep === step.id && (
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse opacity-30"></div>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-1 w-24 mx-4 rounded-full transition-all duration-300 ${
                      step.completed ? 'bg-green-400' : 'bg-white/20'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-white mb-2">
                {steps[currentStep - 1]?.title}
              </h1>
              <p className="text-slate-300">
                {steps[currentStep - 1]?.subtitle}
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-white/20 mb-8">
            {error && (
              <div className="bg-red-500/10 border border-red-400/30 text-red-300 px-6 py-4 rounded-2xl mb-6 backdrop-blur-sm">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                  {error}
                </div>
              </div>
            )}

            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center px-6 py-3 rounded-xl transition-all ${
                currentStep === 1
                  ? 'text-slate-500 cursor-not-allowed'
                  : 'text-white bg-white/10 border border-white/20 hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Previous
            </button>

            <div className="text-center text-slate-400">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < steps.length ? (
              <button
                onClick={nextStep}
                disabled={!validateStep(currentStep)}
                className={`flex items-center px-6 py-3 rounded-xl transition-all ${
                  validateStep(currentStep)
                    ? 'text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                    : 'text-slate-500 bg-slate-600 cursor-not-allowed'
                }`}
              >
                Next
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !validateStep(currentStep)}
                className={`flex items-center px-8 py-3 rounded-xl transition-all ${
                  validateStep(currentStep) && !loading
                    ? 'text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                    : 'text-slate-500 bg-slate-600 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Completing...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Complete Waiver
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressiveWaiverForm;